import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import { EquipmentList } from "@/components/equipment/EquipmentList";
import { useEquipmentStatus } from "@/hooks/equipment/useEquipmentStatus";
import { useCompanyFilter } from "@/hooks/useCompanyFilter";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/contexts/CompanyContext";
import { useAuthenticatedSupabase } from "@/hooks/useAuthenticatedSupabase";
import { useEffect } from "react";

const Equipment = () => {
  const navigate = useNavigate();
  const { handleStatusChange, handleDelete } = useEquipmentStatus();
  const { applyCompanyFilter } = useCompanyFilter();
  const { isAuthenticated, user } = useAuth();
  const { currentCompany } = useCompany();
  const { supabase: authSupabase, isReady } = useAuthenticatedSupabase();

  const { data: equipment, isLoading } = useQuery({
    queryKey: ['equipment', currentCompany?.id],
    queryFn: async () => {
      if (!isAuthenticated) {
        console.log('Equipment: User not authenticated');
        return [];
      }

      console.log('Fetching equipment with company context:', currentCompany?.name);

      try {
        // Use direct table query with company filtering (same approach as useEquipmentQuery)
        let query = authSupabase
          .from('equipment')
          .select('id, name, model, serial_number, location, status, type, company_id, created_at, updated_at');
        
        // Apply company filtering
        query = applyCompanyFilter(query);
        
        query = query.order('name');
        
        const { data, error } = await query;
        
        if (error) {
          console.error('Error fetching equipment:', error);
          throw error;
        }

        console.log(`Retrieved ${data?.length || 0} equipment items`);
        return data || [];
      } catch (error) {
        console.error('Error in equipment query:', error);
        throw error;
      }
    },
    enabled: isAuthenticated && isReady,
    staleTime: 60000, // 1 minute
    refetchOnWindowFocus: false,
  });

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in p-4 md:p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Equipment</h1>
            <p className="text-sm md:text-base text-muted-foreground mt-2">
              View and manage all equipment
            </p>
          </div>
          <Button 
            onClick={() => navigate("/add-equipment")}
            size="lg"
            className="w-full md:w-auto"
          >
            <Plus className="mr-2 h-4 w-4" /> Add Equipment
          </Button>
        </div>

        {isLoading ? (
          <p className="text-center py-4">Loading equipment...</p>
        ) : (
          <EquipmentList 
            equipment={equipment || []}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
          />
        )}
      </div>
    </Layout>
  );
};

export default Equipment;